import * as THREE from 'three';
import type { Course } from '../course/Course';
import type { AssetManager } from '../assets/AssetManager';
import type { Terrain } from '../terrain/Terrain';
import { VegetationField } from '../course/Vegetation';
import { sunDirectionFromAzimuthElevation } from '../lighting/sunMath';
import { MaterialLibrary } from './MaterialLibrary';
import { TerrainRenderer } from './TerrainRenderer';
import { WaterRenderer } from './WaterRenderer';
import { PropRenderer } from './PropRenderer';
import { BiomeRenderer } from './BiomeRenderer';
import { VegetationRenderer } from '../course/VegetationRenderer';
import { BunkerRenderer } from './BunkerRenderer';

/**
 * Consumes a Course and turns it into the actual scene: terrain (with
 * fairway/rough/green/sand blending and heightfield sculpting delegated to
 * TerrainRenderer), water hazards, decorative props, and vegetation
 * (combining hand-placed instances with biome-populated ones). This is
 * what Course.load() delegates to, so Course's own code stays focused on
 * owning/exposing data rather than building meshes.
 *
 * Every sub-renderer here consumes Course/Hole/Biome/VegetationField/
 * WaterFeature data — none of them invent geometry independent of it.
 * Terrain is built first since prop/vegetation placement samples its real
 * height to sit correctly on the (now sculpted, rolling) ground.
 */
export class CourseRenderer {
  private readonly waterRenderer = new WaterRenderer();
  private readonly propRenderer = new PropRenderer();
  private readonly vegetationRenderer = new VegetationRenderer();
  private readonly biomeRenderer = new BiomeRenderer();
  private readonly bunkerRenderer = new BunkerRenderer();

  async render(course: Course, assets: AssetManager, root: THREE.Object3D): Promise<Terrain> {
    const materials = new MaterialLibrary(assets.materials);

    const terrainRenderer = new TerrainRenderer(materials);
    const terrain = terrainRenderer.render(course);
    root.add(terrain.object3D);

    this.waterRenderer.render(
      course.waterFeatures,
      materials,
      sunDirectionFromAzimuthElevation(
        course.lighting.sunAzimuthDeg,
        course.lighting.sunElevationDeg,
      ),
      root,
      terrain,
    );
    this.bunkerRenderer.renderOverlays(course, terrain, materials, root);
    this.propRenderer.render(course, terrain, materials, root);

    const combinedVegetation = new VegetationField();
    combinedVegetation.addMany(course.vegetation.all.slice());
    for (const biome of course.biomes) {
      combinedVegetation.addMany(this.biomeRenderer.populate(course, biome).all.slice());
    }
    await this.vegetationRenderer.realize(combinedVegetation, assets, terrain, root);

    return terrain;
  }

  /** Advances per-frame visual animation (currently: water ripple time). */
  update(deltaSeconds: number): void {
    this.waterRenderer.update(deltaSeconds);
  }

  dispose(): void {
    this.waterRenderer.dispose();
    this.bunkerRenderer.dispose();
    this.propRenderer.dispose();
    this.vegetationRenderer.dispose();
  }
}
